import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// APOL1 high-risk genotype + hypertension + albuminuria — the normal creatinine that
// masked early kidney injury.

export const kidneyMalcolm: Scenario = {
  slug: 'kidney-malcolm',
  series: 'Cardiovascular Risk',
  title: 'His kidney number was normal. The leak was not.',
  socialHook:
    "Malcolm, 39: normal creatinine, normal eGFR, family history of dialysis. The clue was albumin in his urine — the early leak that changed the whole story.",
  meta: {
    description:
      "A fictional case study: Malcolm has normal kidney filtration labs but persistent urine albumin, borderline hypertension, and an APOL1 high-risk genotype. His story shows why eGFR is not the whole story and albumin can be the early warning.",
  },

  persona: {
    name: 'Malcolm',
    age: 39,
    sex: 'Male',
    ancestry: 'African American',
    occupation: 'High school assistant principal',
    chiefConcern:
      "His father started dialysis at 56. Malcolm's routine labs look normal: creatinine 1.0 mg/dL, eGFR 92. But his urine albumin-creatinine ratio has been high twice, and his blood pressure sits around 138/86. He was told to watch it and repeat next year. He wants to know whether his family history makes that too casual.",
    familyHistory:
      'Father: kidney failure and dialysis at 56. Paternal aunt: hypertension and chronic kidney disease. Mother: type 2 diabetes, no kidney disease.',
  },

  clinicalPicture: {
    symptoms: [
      'No symptoms — feels well and exercises occasionally',
      'Borderline blood pressure over three routine visits',
      'Persistent urine albumin on two tests three months apart',
      'Anxiety about repeating his father’s dialysis story',
    ],
    labs: [
      { name: 'Creatinine', value: '1.0 mg/dL', refRange: '0.7–1.3 mg/dL', flag: 'normal' },
      { name: 'eGFR', value: '92 mL/min/1.73m²', refRange: '>90', flag: 'normal' },
      { name: 'Urine albumin-creatinine ratio', value: '84 mg/g', refRange: '<30 mg/g', flag: 'high' },
      { name: 'Blood Pressure', value: '138/86 mmHg', refRange: '<130/80 mmHg', flag: 'high' },
    ],
    medications: ['No prescription medications'],
    supplements: ['Multivitamin occasionally'],
    lifestyle: [
      'Desk-heavy workday, walks on weekends',
      'High-sodium restaurant meals 3–4 times/week',
      'Non-smoker',
      'Alcohol: 2–4 drinks/week',
      'Sleep: 6–7 hours, often interrupted by work stress',
    ],
  },

  genetics: [
    {
      gene: 'APOL1',
      variant: 'G1/G2 kidney-risk genotype',
      rsid: 'rs73885319 / rs71785313',
      genotype: 'Two APOL1 risk alleles',
      plainMeaning:
        "Malcolm's result does not mean kidney failure is certain. It means blood pressure and urine albumin deserve earlier attention than they would for an average patient.",
      technicalDetail:
        "APOL1 risk alleles are common in people with recent West African ancestry because they historically protected against trypanosome infection. Carrying two risk alleles substantially raises susceptibility to several forms of kidney disease, especially when second hits such as hypertension, viral illness, inflammation, or metabolic stress are present. APOL1 is not destiny: many two-risk-allele carriers never develop kidney failure. But persistent albuminuria in this context is not a minor incidental finding — it is an early sign that the kidney filter may already be under stress.",
    },
    {
      gene: 'UMOD',
      variant: 'Promoter risk variant',
      rsid: 'rs12917707',
      genotype: 'Higher uromodulin expression pattern',
      plainMeaning:
        "His kidneys may hold on to more salt, quietly pushing blood pressure and pressure inside the kidney filter upward.",
      technicalDetail:
        "UMOD encodes uromodulin, a kidney tubule protein involved in sodium handling and blood pressure regulation. Risk patterns associated with higher uromodulin expression can increase sodium reabsorption and raise susceptibility to hypertension-mediated kidney injury. In Malcolm, this makes dietary sodium and blood pressure control more kidney-specific interventions, not generic lifestyle advice.",
    },
    {
      gene: 'AGT',
      variant: 'M235T',
      rsid: 'rs699',
      genotype: 'T allele carrier',
      plainMeaning:
        "His blood pressure system is biased toward higher pressure. Even a mildly high reading may put extra force on a vulnerable kidney filter.",
      technicalDetail:
        "AGT produces angiotensinogen, the precursor to angiotensin II. The M235T T allele is associated with higher angiotensinogen activity and salt-sensitive blood pressure. Angiotensin II raises pressure inside the glomerulus — the kidney’s filtration unit — which can worsen albumin leakage over time. This is why albuminuria plus borderline hypertension deserves earlier attention than creatinine alone suggests.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'The normal result was not the full answer',
      body: "Malcolm's father started dialysis before retirement age. So when Malcolm saw normal creatinine and eGFR on his own labs, he felt reassured. His family doctor was reassured too. But another result kept showing up: albumin in the urine. It did not cause pain, swelling, or fatigue. It was just a leak — easy to miss if you only look at filtration, but important if you know how kidney damage can start.",
    },
    {
      type: 'narrative',
      heading: 'eGFR measures flow. Albumin shows the leak.',
      body: "Creatinine and eGFR answer one question: how much filtering capacity is left today? Albumin-creatinine ratio asks a different question: is the kidney filter leaking protein it should keep in the blood? Those timelines do not always move together. A person can have normal filtration while the filter wall is already under stress. Malcolm's normal eGFR is good news. It is not the whole story.",
    },
    {
      type: 'reveal',
      heading: 'The urine leak mattered more in his case',
      body: "The albumin result looks mild until you add the family story and genetics. Malcolm carries two APOL1 kidney-risk alleles. That does not diagnose kidney disease or make kidney failure inevitable. It does lower the threshold for action. Persistent albuminuria, borderline blood pressure, and a family history of dialysis now point to one picture: the kidney filter may be under pressure before filtration has fallen.",
    },
    {
      type: 'reveal',
      heading: 'His blood pressure was also kidney pressure',
      body: "The blood pressure number is not only about heart risk. UMOD points toward salt retention, and AGT points toward a hormone pathway that can raise pressure inside the glomerulus, the kidney's filter. For many people, 138/86 is a cardiovascular warning. For Malcolm, it may also be mechanical stress on a genetically vulnerable filter. The target changes: protect the kidney before eGFR starts to fall.",
    },
    {
      type: 'action',
      heading: 'Five conversations worth having now',
      items: [
        'Repeat the urine albumin-creatinine ratio to confirm the leak is persistent. One abnormal result can be noise; two or more over 3–6 months is a kidney-risk signal.',
        'Ask for a nephrology referral based on APOL1 high-risk genotype, persistent albuminuria, and family history of kidney failure. The goal is early prevention, not late rescue.',
        'Treat blood pressure as kidney protection. Ask whether an ACE inhibitor or ARB is appropriate, since these medications can reduce albuminuria and lower pressure inside the kidney filter.',
        'Reduce sodium and track blood pressure at home. With UMOD and AGT pointing toward salt-sensitive pressure, sodium reduction is targeted treatment for his mechanism.',
        'Avoid avoidable kidney stressors: frequent NSAID use, dehydration during illness, and unmonitored high-protein supplement routines. For Malcolm, kidney reserve is something to preserve deliberately.',
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: 'Why normal eGFR missed the leak',
      caption:
        'Creatinine says filtration is preserved. Albuminuria plus APOL1 says the filter may already be under stress. These levers focus on preserving reserve early.',
      baselineLabel: 'Normal eGFR + persistent albuminuria + APOL1 high-risk genotype',
      baseline: 72,
      levers: [
        {
          label: 'Confirm albuminuria and refer early',
          delta: -16,
          description:
            'Early kidney specialist input can change monitoring and prevention before filtration declines.',
        },
        {
          label: 'Lower BP with kidney-protective strategy',
          delta: -18,
          description:
            'ACE inhibitor or ARB therapy can reduce glomerular pressure and albumin leakage when clinically appropriate.',
        },
        {
          label: 'Sodium reduction for salt-sensitive pressure',
          delta: -12,
          description:
            'UMOD and AGT make sodium intake part of the mechanism, not generic lifestyle advice.',
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'kidney-malcolm',
    label: 'Explore your kidney and blood pressure genetics in Coach',
  },
}
